"use client";

import React, { useEffect, useState } from "react";
import { CircleCheck as CheckCircle2, MessageSquareText, Star, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
export default function ReviewSubmission() {
  const [servedAt, setServedAt] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    getWvcClient()?.pluginRequest("wvc-custom-records/v1/collections/testimonials/schema").then((schema: any) => {
      if (schema?.served_at) {
        setServedAt(schema.served_at);
      }
    }).catch(() => {
      // Fallback or leave null if schema fetch has issue
    });
  }, []);
  const parseFieldErrors = (message: string) => {
    const parts = (message.split("—")[1] ?? "").split(";").map(p => p.trim().replace(/\.$/, "").split(":").map(s => s.trim())).filter(p => p.length === 2);
    return Object.fromEntries(parts);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setFieldErrors({});
    if (!name.trim()) {
      setFieldErrors(prev => ({
        ...prev,
        name: "يرجى كتابة الاسم"
      }));
      return;
    }
    if (!comment.trim()) {
      setFieldErrors(prev => ({
        ...prev,
        comment: "يرجى كتابة رأيك وتجربتك"
      }));
      return;
    }
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        name: name.trim(),
        rating,
        comment: comment.trim()
      };
      if (servedAt) {
        payload._served_at = servedAt;
      }
      payload._honeypot = honeypot;
      await getWvcClient()?.pluginRequest("wvc-custom-records/v1/collections/testimonials/records", {
        method: "POST",
        data: payload
      });
      setSubmitted(true);
    } catch (err: any) {
      if (err?.status === 400 && err?.code === "wvccr_invalid_field" && err?.message) {
        setFieldErrors(parseFieldErrors(err.message));
        setErrorMessage("يرجى التأكد من صحة البيانات المدخلة.");
      } else if (err?.status === 400 && err?.code === "wvccr_submission_refused") {
        setErrorMessage("تعذر قبول الإرسال حالياً. يرجى المحاولة لاحقاً.");
      } else if (err?.status === 429) {
        setErrorMessage("يرجى الانتظار قليلاً قبل المحاولة مرة أخرى.");
      } else {
        setErrorMessage(err?.message || "حدث خطأ أثناء إرسال تقييمك. يرجى المحاولة مجدداً.");
      }
    } finally {
      setLoading(false);
    }
  };
  return <section id="reviewsubmission" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <div className="text-center mb-10 space-y-3">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-2">
            <MessageSquareText className="size-6" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            شاركنا تجربتك وتقييمك
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            رأيك يهمنا في لمسة احتراف ويساعدنا على تقديم أفضل مستوى لخدمات صيانة وتنظيف الأفران بالرياض.
          </p>
        </div>

        <Card className="border border-border shadow-md bg-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-semibold text-foreground">
              نموذج إضافة التقييم
            </CardTitle>
            <CardDescription className="text-muted-foreground text-sm">
              أدخل بياناتك وتقييمك الصادق لخدمتنا.
            </CardDescription>
          </CardHeader>

          <CardContent>
            {submitted ? <div className="py-8 text-center space-y-4">
                <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary/10 text-primary">
                  <CheckCircle2 className="size-8" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">
                  شكراً لمشاركتك تقييمك!
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto text-sm md:text-base leading-relaxed">
                  تم استلام تقييمك بنجاح، وسيظهر على الموقع بعد مراجعته من قبل فريق العمل.
                </p>
                <div className="pt-4">
                  <Button variant="outline" onClick={() => {
                setSubmitted(false);
                setName("");
                setRating(5);
                setComment("");
              }}>
                    إرسال تقييم آخر
                  </Button>
                </div>
              </div> : <form onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>تنبيه</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>}

                {/* Honeypot field (hidden from legitimate users) */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="testimonials-honeypot">اترك هذا الحقل فارغاً</label>
                  <input id="testimonials-honeypot" type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
                </div>

                {/* Rating selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    التقييم العام
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5" role="radiogroup" aria-label="اختر التقييم">
                      {[1, 2, 3, 4, 5].map(starValue => {
                    const activeRating = hoverRating !== null ? hoverRating : rating;
                    const isFilled = starValue <= activeRating;
                    return <Button key={starValue} type="button" variant="ghost" size="icon" role="radio" aria-checked={rating === starValue} aria-label={`${starValue} من 5 نجوم`} className="p-1 h-auto w-auto rounded-sm text-amber-500 hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform hover:scale-110" onClick={() => setRating(starValue)} onMouseEnter={() => setHoverRating(starValue)} onMouseLeave={() => setHoverRating(null)}>
                            <Star className={`size-7 transition-colors ${isFilled ? "fill-primary text-primary" : "text-muted-foreground/30 fill-transparent"}`} />
                          </Button>;
                  })}
                    </div>
                    <span className="text-sm font-medium text-muted-foreground mr-2">
                      ({rating} من 5)
                    </span>
                  </div>
                </div>

                {/* Name Input */}
                <div className="space-y-2">
                  <Label htmlFor="review-name" className="text-sm font-medium text-foreground">
                    الاسم الكامل <span className="text-destructive">*</span>
                  </Label>
                  <Input id="review-name" type="text" placeholder="مثال: محمد العمري" value={name} onChange={e => setName(e.target.value)} disabled={loading} className={fieldErrors.name ? "border-destructive ring-destructive/20" : ""} />
                  {fieldErrors.name && <p className="text-destructive text-xs mt-1">
                      {fieldErrors.name === "required" ? "الاسم مطلوب" : fieldErrors.name}
                    </p>}
                </div>

                {/* Comment Textarea */}
                <div className="space-y-2">
                  <Label htmlFor="review-comment" className="text-sm font-medium text-foreground">
                    رأيك وتجربتك <span className="text-destructive">*</span>
                  </Label>
                  <Textarea id="review-comment" rows={4} placeholder="أخبرنا عن تجربتك في غسيل أو صيانة الفرن، ومدى رضاك عن دقة المواعيد وجودة الخدمة..." value={comment} onChange={e => setComment(e.target.value)} disabled={loading} className={fieldErrors.comment ? "border-destructive ring-destructive/20" : ""} />
                  {fieldErrors.comment && <p className="text-destructive text-xs mt-1">
                      {fieldErrors.comment === "required" ? "التعليق مطلوب" : fieldErrors.comment}
                    </p>}
                </div>

                {/* Submit Button */}
                <Button type="submit" disabled={loading} className="w-full text-base font-semibold py-2.5 h-auto cursor-pointer">
                  {loading ? "جاري الإرسال..." : "إرسال التقييم"}
                </Button>
              </form>}
          </CardContent>
        </Card>
      </div>
    </section>;
}